import HarmonicsData from '../harmonics/harmonics';
import type { HarmonicSequenceValue } from '../harmonics/harmonics';
import PPTSpur from '../ppt-spur';
import FilterService from 'simdds/services/filter';
import { linearSpacing } from '../../utils';

export interface SpurGraphLimits {
  minfreq: number;
  maxfreq: number;
  intervalfreq: number;
  mindb: number;
  maxdb: number;
  intervaldb: number;
}

export default class SpurGraphModel {
  private readonly systemClock: number;
  private readonly harmonicsData: HarmonicsData;
  private readonly pptSpur: PPTSpur;
  private readonly filter?: FilterService;

  constructor(systemClock: number, harmonicsData: HarmonicsData, pptSpur: PPTSpur, filter?: FilterService) {
    this.systemClock = systemClock;
    this.harmonicsData = harmonicsData;
    this.pptSpur = pptSpur;
    if (filter) {
      this.filter = filter;
    }
  }

  get limits(): SpurGraphLimits {
    const systemClock = this.systemClock;
    return {
      minfreq: 0.0,
      maxfreq: systemClock * 2.5,
      intervalfreq: systemClock / 2.0,
      mindb: -120.0,
      maxdb: 0.0,
      intervaldb: 20,
    };
  }

  get data(): number[][][] {
    const dac1 = this.dac1;
    const dac2 = this.dac2;
    const dac3 = this.dac3;
    const ppt = this.ppt;
    const fundamental = this.fundamental;
    const filterResponse = this.filterResponse;

    const result = [dac1, dac2, dac3, ppt, fundamental, filterResponse];
    return result;
  }

  private get dac1(): number[][] {
    const dacImages = this.harmonicsData.rawHarmonicsTable[0] as HarmonicSequenceValue[];
    const result = [];
    let onFirst = true; //Skipping first value
    for (const dacImage of dacImages) {
      if (onFirst) {
        onFirst = false;
      } else {
        result.push([parseFloat(dacImage.sequenceFrequency), parseFloat(dacImage.sequenceAmplitude)]);
      }
    }

    return result;
  }

  private get dac2(): number[][] {
    const dacImages = this.harmonicsData.rawHarmonicsTable[1] as HarmonicSequenceValue[];
    const result = [];
    for (const dacImage of dacImages) {
      result.push([parseFloat(dacImage.sequenceFrequency), parseFloat(dacImage.sequenceAmplitude)]);
    }

    return result;
  }

  private get dac3(): number[][] {
    const dacImages = this.harmonicsData.rawHarmonicsTable[2] as HarmonicSequenceValue[];
    const result = [];
    for (const dacImage of dacImages) {
      result.push([parseFloat(dacImage.sequenceFrequency), parseFloat(dacImage.sequenceAmplitude)]);
    }

    return result;
  }

  private get ppt(): number[][] {
    const pptSpur = [];
    pptSpur.push(this.pptSpur.PPTSpur1);
    pptSpur.push(this.pptSpur.PPTSpur2);
    const result = [];
    for (const pptItem of pptSpur) {
      const sequenceFrequency = pptItem.sequenceFrequency;
      const sequenceAmplitude = pptItem.sequenceAmplitude;
      if (sequenceFrequency != null && sequenceAmplitude != null) {
        result.push([parseFloat(sequenceFrequency), parseFloat(sequenceAmplitude)]);
      }
    }

    return result;
  }

  private get fundamental(): number[][] {
    const dacImages = this.harmonicsData.rawHarmonicsTable[0] as HarmonicSequenceValue[];
    const fundamental = dacImages[0] as HarmonicSequenceValue;
    const result = [];
    result.push([parseFloat(fundamental.sequenceFrequency), parseFloat(fundamental.sequenceAmplitude)]);

    return result;
  }

  private get filterResponse(): number[][] {
    const showFilter = this.filter ? this.filter.displayFilters : false;
    const systemClock = this.systemClock;
    let result = [];

    if (showFilter) {
      const stepCount = 100;
      result = this.getFilterResponseLine(systemClock, stepCount);
    } else {
      result.push([], []);
    }

    return result;
  }

  private getFilterResponseLine(systemClock: number, stepCount: number) {
    const frequencyDomain = linearSpacing(0.0, systemClock * 2.5, stepCount);

    const result = [];
    if (this.filter) {
      for (const frequency of frequencyDomain) {
        const filterValues = this.filter.applyFilter(frequency);
        result.push([frequency, filterValues.filterAmplitude || 0]);
      }

      const filterResponseValue = this.filter.applyFilter(systemClock * 2.5);
      result.push([systemClock * 2.5, filterResponseValue.filterAmplitude || 0]);
    }
    return result;
  }
}
